import { Group, Rect, Circle, Text } from "react-konva";
import { useKonvaImage } from "../../hooks/useKonvaImage";
import { Image as KonvaImage } from "react-konva";

const PORTRAIT_R = 27;
const BTN_W = 76;
const BTN_H = 25;
const GAP_X = 6;
const GAP_Y = 6;
const PORTRAIT_GAP = 8;

// Carved-wood "pill button" hovering menu that pops up above a clicked token (NPC, chest, or
// another player's token) — a circular portrait of the target up top (matching the token's own
// art, or a fallback initial-letter medallion), with up to 6 oval carved action buttons arranged
// in a compact 1-2 row grid underneath. `actions` is a list of { label, testId, onClick, danger },
// already pre-filtered by the caller for line-of-sight + range — purely presentational/layout here.
export default function TokenContextMenu({ x, y, actions, testId, token }) {
  const image = useKonvaImage(token?.image_url);
  if (!actions || actions.length === 0) return null;

  const n = actions.length;
  const perRow = n <= 3 ? n : Math.ceil(n / 2);
  const rows = Math.ceil(n / perRow);
  const buttonsWidth = perRow * BTN_W + (perRow - 1) * GAP_X;
  const buttonsHeight = rows * BTN_H + (rows - 1) * GAP_Y;
  const portraitD = PORTRAIT_R * 2;
  const menuWidth = Math.max(buttonsWidth, portraitD);
  const menuHeight = portraitD + PORTRAIT_GAP + buttonsHeight;

  const rowsOfActions = [];
  for (let i = 0; i < n; i += perRow) rowsOfActions.push(actions.slice(i, i + perRow));

  return (
    <Group data-testid={testId || "token-context-menu"} x={x - menuWidth / 2} y={y - menuHeight}>
      {/* Circular carved-wood portrait medallion of the target token */}
      <Group x={menuWidth / 2} y={PORTRAIT_R}>
        <Circle radius={PORTRAIT_R + 4} fill="#241407" stroke="#D4AF37" strokeWidth={2} shadowColor="#000000" shadowBlur={10} shadowOpacity={0.6} />
        <Circle radius={PORTRAIT_R + 1} stroke="#8a6d1f" strokeWidth={1} dash={[3, 3]} opacity={0.8} />
        <Group clipFunc={(ctx) => ctx.arc(0, 0, PORTRAIT_R, 0, Math.PI * 2, false)}>
          {image ? (
            <KonvaImage image={image} x={-PORTRAIT_R} y={-PORTRAIT_R} width={portraitD} height={portraitD} />
          ) : (
            <>
              <Circle radius={PORTRAIT_R} fill="#3a3335" />
              <Text
                text={(token?.name || "?").slice(0, 1).toUpperCase()}
                fontSize={PORTRAIT_R}
                fill="#D4AF37"
                width={portraitD}
                height={portraitD}
                x={-PORTRAIT_R}
                y={-PORTRAIT_R * 0.7}
                align="center"
                fontFamily="Cormorant Garamond"
              />
            </>
          )}
        </Group>
      </Group>

      {/* Carved oval action buttons, 1-3 per row, up to 2 rows */}
      {rowsOfActions.map((rowActions, rowIdx) => {
        const rowWidth = rowActions.length * BTN_W + (rowActions.length - 1) * GAP_X;
        const rowStartX = (menuWidth - rowWidth) / 2;
        const rowY = portraitD + PORTRAIT_GAP + rowIdx * (BTN_H + GAP_Y);
        return rowActions.map((a, idx) => (
          <Group
            key={a.testId || a.label}
            data-testid={a.testId}
            x={rowStartX + idx * (BTN_W + GAP_X)}
            y={rowY}
            onClick={(e) => { e.cancelBubble = true; a.onClick(); }}
            onTap={(e) => { e.cancelBubble = true; a.onClick(); }}
          >
            <Rect
              width={BTN_W}
              height={BTN_H}
              cornerRadius={BTN_H / 2}
              fillLinearGradientStartPoint={{ x: 0, y: 0 }}
              fillLinearGradientEndPoint={{ x: 0, y: BTN_H }}
              fillLinearGradientColorStops={[0, "#3a2414", 1, "#20130a"]}
              stroke={a.danger ? "#D90429" : "#D4AF37"}
              strokeWidth={1.4}
              shadowColor="#000000"
              shadowBlur={6}
              shadowOpacity={0.45}
            />
            <Text
              text={a.label}
              width={BTN_W}
              height={BTN_H}
              align="center"
              verticalAlign="middle"
              fontSize={11}
              fontStyle="bold"
              fill={a.danger ? "#e8536b" : "#D4AF37"}
            />
          </Group>
        ));
      })}
    </Group>
  );
}
